/* Optional browser dictation for Siddabot. No audio/backend integration and no
   automatic sending. Start only from an explicit mic-button gesture.
   attach({input,button,status,form}) works with an input or textarea.
   Call destroy() on chat close and setEnabled(!busy) with the chat's busy state. */
(function (global) {
  'use strict';
  if (global.SiddabotVoice) return;
  function attach(options) {
    var input = options.input, button = options.button, status = options.status;
    if (!input || !button) throw new Error('Voice input needs a text field and mic button.');
    var doc = input.ownerDocument || global.document;
    var form = options.form || input.form;
    var Recognition = global.SpeechRecognition || global.webkitSpeechRecognition;
    var supported = typeof Recognition === 'function' && global.isSecureContext !== false;
    var enabled = true, destroyed = false, writing = false, active = null;
    var emptyHint = 'Tap the mic to dictate, then review before sending.';
    button.type = 'button';
    if (status) { status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite'); }
    function say(message, state) {
      if (status) status.textContent = message;
      if (typeof options.onState === 'function') options.onState({ state: state, message: message });
    }
    function render() {
      var state = active ? active.phase : supported ? 'idle' : 'unsupported';
      button.disabled = destroyed || !supported || !enabled || input.disabled || input.readOnly || state === 'stopping';
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', active ? 'Stop voice input' : supported ? 'Start voice input' : 'Voice input unavailable');
      button.setAttribute('data-voice-state', state);
      button.title = active ? 'Stop dictation. Review the text before sending.' : 'Dictate with your browser. It may use an online speech service.';
      if (button.classList) button.classList.toggle('is-listening', !!active);
    }
    function clearTimers(session) {
      if (!session) return;
      global.clearTimeout(session.durationTimer); global.clearTimeout(session.stopTimer);
    }
    function current(session) { return !destroyed && active === session; }
    function retire(message, state, cancel) {
      var session = active; active = null; clearTimers(session);
      // Retire before abort: synchronous or late recognizer callbacks are ignored.
      if (session && cancel) { try { session.recognition.abort(); } catch (error) {} }
      render(); if (message !== undefined) say(message, state || 'idle');
    }
    function abort(message) { retire(message === undefined ? 'Voice input stopped. You can keep typing.' : message, 'idle', true); }
    function stop() {
      var session = active;
      if (!session || session.phase === 'stopping') return false;
      session.phase = 'stopping'; global.clearTimeout(session.durationTimer);
      render(); say('Finishing voice input…', 'stopping');
      try { session.recognition.stop(); }
      catch (error) { abort(); return false; }
      if (current(session)) session.stopTimer = global.setTimeout(function () { if (current(session)) abort(); }, 3000);
      return true;
    }
    function insertFinal(session, transcript) {
      // If typing or a programmatic reset changed the field, do not overwrite it.
      if (input.value !== session.lastWritten) { abort('Voice input stopped while you edit. You can keep typing.'); return; }
      var before = session.prefix && !/\s$/.test(session.prefix) && !/^[,.;:!?]/.test(transcript) ? ' ' : '';
      var after = session.suffix && !/^[\s,.;:!?]/.test(session.suffix) ? ' ' : '';
      var limit = Number(input.maxLength), available = limit >= 0 ? limit - session.prefix.length - session.suffix.length - before.length - after.length : Infinity;
      var clipped = transcript.slice(0, Math.max(0, available)).replace(/[\uD800-\uDBFF]$/, '').trimEnd();
      if (!clipped) { abort('Your message is full. Edit it or send it before adding more.'); return; }
      var value = session.prefix + before + clipped + after + session.suffix;
      session.lastWritten = value; session.hasFinal = true;
      writing = true;
      try {
        input.value = value;
        if (typeof input.setSelectionRange === 'function') input.setSelectionRange(session.prefix.length + before.length + clipped.length, session.prefix.length + before.length + clipped.length);
        input.dispatchEvent(new global.Event('input', { bubbles: true, composed: true }));
      } finally { writing = false; }
      if (clipped !== transcript) abort('Message limit reached. Review your text before sending.');
    }
    function start() {
      if (destroyed || !supported || !enabled || input.disabled || input.readOnly || doc.hidden || active) return false;
      var value = String(input.value || '');
      var selectionStart = Number.isInteger(input.selectionStart) ? input.selectionStart : value.length;
      var selectionEnd = Number.isInteger(input.selectionEnd) ? input.selectionEnd : selectionStart;
      var recognition;
      try { recognition = new Recognition(); }
      catch (error) { say('Voice input is unavailable in this browser. You can keep typing.', 'error'); return false; }
      var session = { recognition: recognition, prefix: value.slice(0, selectionStart), suffix: value.slice(selectionEnd), lastWritten: value, finals: {}, hasFinal: false, phase: 'starting' };
      active = session;
      recognition.lang = 'en-AU'; recognition.continuous = false; recognition.interimResults = true; recognition.maxAlternatives = 1;
      recognition.onstart = function () {
        if (!current(session)) return;
        if (doc.hidden || !enabled || input.disabled || input.readOnly) { abort(); return; }
        if (session.phase !== 'stopping') { session.phase = 'listening'; render(); say('Listening… tap the mic to stop. Nothing is sent until you press Send.', 'listening'); }
      };
      recognition.onresult = function (event) {
        if (!current(session)) return;
        if (doc.hidden || !enabled || input.disabled || input.readOnly) { abort(); return; }
        for (var i = Number(event.resultIndex) || 0; i < event.results.length; i++) {
          var result = event.results[i];
          if (result.isFinal) session.finals[i] = String(result[0] && result[0].transcript || '').trim();
        }
        var transcript = Object.keys(session.finals).sort(function (a, b) { return Number(a) - Number(b); }).map(function (key) { return session.finals[key]; }).filter(Boolean).join(' ');
        if (transcript) insertFinal(session, transcript);
      };
      recognition.onerror = function (event) {
        if (!current(session)) return;
        var messages = {
          'not-allowed': 'Microphone permission was declined. You can keep typing.',
          'service-not-allowed': 'Voice input is not allowed in this browser. You can keep typing.',
          'audio-capture': 'No microphone is available. You can keep typing.',
          'network': 'Voice input could not connect. You can keep typing.',
          'no-speech': 'No speech was heard. Tap the mic to try again, or type.',
          'language-not-supported': 'Australian English dictation is unavailable in this browser. You can keep typing.',
          'aborted': 'Voice input stopped. You can keep typing.'
        };
        retire(messages[event.error] || 'Voice input did not work. You can keep typing.', 'error', true);
      };
      recognition.onnomatch = function () { if (current(session)) retire('That speech was not recognised. Tap the mic to try again, or type.', 'error', true); };
      recognition.onend = function () { if (current(session)) retire(session.hasFinal ? 'Voice text added. Review it before sending.' : 'No speech was added. Tap the mic to try again, or type.', 'idle', false); };
      render(); say('Starting voice input… your browser may ask for microphone permission.', 'starting');
      try { recognition.start(); }
      catch (error) { retire('Voice input could not start. You can keep typing.', 'error', true); return false; }
      // No automatic restart; one utterance or at most 45 seconds per explicit tap.
      if (current(session)) session.durationTimer = global.setTimeout(function () { if (current(session)) stop(); }, 45000);
      return true;
    }
    function clicked() { if (active) stop(); else start(); }
    function edited() { if (!writing && active) abort('Voice input stopped while you edit. You can keep typing.'); }
    function hidden() { if (doc.hidden && active) abort('Voice input stopped because the page is hidden.'); }
    function leaving() { if (active) abort(''); }
    function submitted() { if (active) abort(''); }
    button.addEventListener('click', clicked);
    input.addEventListener('input', edited);
    if (form) form.addEventListener('submit', submitted, true);
    doc.addEventListener('visibilitychange', hidden);
    global.addEventListener('pagehide', leaving);
    render(); say(supported ? emptyHint : 'Voice input is not supported in this browser. You can type your message.', supported ? 'idle' : 'unsupported');
    return {
      supported: supported, start: start, stop: stop, abort: abort,
      setEnabled: function (value) { enabled = !!value; if (!enabled && active) abort(''); render(); },
      destroy: function () {
        if (destroyed) return;
        abort(''); destroyed = true;
        button.removeEventListener('click', clicked); input.removeEventListener('input', edited);
        if (form) form.removeEventListener('submit', submitted, true);
        doc.removeEventListener('visibilitychange', hidden); global.removeEventListener('pagehide', leaving); render();
      }
    };
  }
  global.SiddabotVoice = Object.freeze({ attach: attach });
})(window);

/* Siddabot side dock. No network calls and no changes to the shop's own controls.
 * Call mount after creating the current widget; call refresh after its layout changes.
 * Core shipping/cart/backtop selectors were verified against MAIN 194237399319
 * on 28 Oct; additional wishlist/PLA/toast hooks came from the 24 Oct snapshot. */
(function (scope) {
  'use strict';

  var OBSTACLES = [
    '#sbPill', '#effectiveAppsScrollToTopBtn',
    '.sd-satc[data-sd-satc-show]', '#sd-added-toast.sd-show'
  ];
  var MODALS = [
    'cart-drawer.active', 'cart-notification.active',
    '#sbPanel.on', '#sbOv.on',
    '.sd-wld.is-open', '.sd-wld-backdrop.is-open', '.sd-plv.open',
    'dialog[open]', '[role="dialog"][aria-modal="true"]'
  ];

  function overlap(a, b, gap) {
    return a.left < b.right + gap && a.right > b.left - gap &&
      a.top < b.bottom + gap && a.bottom > b.top - gap;
  }

  // Coordinates share getBoundingClientRect's layout-viewport coordinate space.
  function place(input) {
    var view = input.viewport;
    var width = input.width, height = input.height;
    var edge = input.edge == null ? 16 : input.edge;
    var gap = input.gap == null ? 12 : input.gap;
    var topInset = input.topInset == null ? Math.min(96, view.height * .2) : input.topInset;
    var minY = view.top + topInset;
    var maxY = view.top + view.height - height - edge;
    var left = view.left + view.width - width - edge;
    if (width <= 0 || height <= 0 || left < view.left + edge || maxY < minY) return null;
    var desired = Math.max(minY, Math.min(maxY, view.top + view.height * .55 - height / 2));
    var obstacles = input.obstacles || [];
    var candidates = [desired, minY, maxY];
    obstacles.forEach(function (r) {
      candidates.push(r.top - height - gap, r.bottom + gap);
    });
    candidates = candidates.filter(function (y) {
      return Number.isFinite(y) && y >= minY && y <= maxY;
    }).sort(function (a, b) { return Math.abs(a - desired) - Math.abs(b - desired); });
    for (var i = 0; i < candidates.length; i++) {
      var rect = { left: left, right: left + width, top: candidates[i], bottom: candidates[i] + height };
      if (!obstacles.some(function (r) { return overlap(rect, r, gap); })) return { left: left, top: candidates[i] };
    }
    return null;
  }

  function mount(options) {
    options = options || {};
    var launch = options.launcher;
    if (!launch || !launch.ownerDocument) throw new TypeError('A launcher element is required');
    var doc = launch.ownerDocument, win = doc.defaultView;
    var obstacleSelectors = options.obstacleSelectors || OBSTACLES;
    var modalSelectors = options.modalSelectors || MODALS;
    var styleKeys = ['position', 'top', 'left', 'right', 'bottom', 'transform', 'visibility', 'pointer-events'];
    var original = {};
    styleKeys.forEach(function (key) {
      original[key] = [launch.style.getPropertyValue(key), launch.style.getPropertyPriority(key)];
    });
    var safe = doc.createElement('i');
    safe.setAttribute('aria-hidden', 'true');
    safe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;width:0;height:0;padding-right:env(safe-area-inset-right,0px);padding-bottom:env(safe-area-inset-bottom,0px)';
    (doc.body || doc.documentElement).appendChild(safe);
    var stopped = false, frame = null, suspended = false;
    var resizeObserver = win.ResizeObserver ? new win.ResizeObserver(refresh) : null;
    var observed = new Set();

    function put(key, value, priority) {
      priority = priority || '';
      if (launch.style.getPropertyValue(key) !== value || launch.style.getPropertyPriority(key) !== priority) {
        launch.style.setProperty(key, value, priority);
      }
    }
    function restore(key) {
      var old = original[key];
      if (old[0]) put(key, old[0], old[1]);
      else if (launch.style.getPropertyValue(key)) launch.style.removeProperty(key);
    }
    function viewport() {
      var vv = win.visualViewport;
      return { left: vv ? vv.offsetLeft : 0, top: vv ? vv.offsetTop : 0,
        width: vv ? vv.width : doc.documentElement.clientWidth,
        height: vv ? vv.height : win.innerHeight };
    }
    function elements(selectors) {
      var found = [];
      selectors.forEach(function (selector) {
        doc.querySelectorAll(selector).forEach(function (element) {
          if (element !== options.host && !found.includes(element)) found.push(element);
        });
      });
      return found;
    }
    function visibleRect(element, view) {
      var cs = win.getComputedStyle(element);
      if (element.hidden || cs.display === 'none' || cs.visibility === 'hidden' || Number(cs.opacity) === 0) return null;
      var r = element.getBoundingClientRect();
      if (!(r.width > 0 && r.height > 0) || r.right <= view.left || r.bottom <= view.top ||
          r.left >= view.left + view.width || r.top >= view.top + view.height) return null;
      return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
    }
    function suppress(value) {
      // Do not alter hidden/display: the chat itself owns those while open.
      if (value) { put('visibility', 'hidden', 'important'); put('pointer-events', 'none', 'important'); }
      else { restore('visibility'); restore('pointer-events'); }
      if (value !== suspended) {
        suspended = value;
        if (typeof options.onSuspend === 'function') options.onSuspend(value);
      }
    }
    function update() {
      frame = null;
      if (stopped) return;
      var view = viewport();
      var modals = elements(modalSelectors);
      var obstacles = elements(obstacleSelectors);
      if (resizeObserver) {
        var current = new Set([launch].concat(modals, obstacles));
        observed.forEach(function (element) { if (!current.has(element)) resizeObserver.unobserve(element); });
        current.forEach(function (element) { if (!observed.has(element)) resizeObserver.observe(element); });
        observed = current;
      }
      if (modals.some(function (element) { return visibleRect(element, view); })) { suppress(true); return; }
      // The widget may hide its launcher while open. Never temporarily reveal it to measure.
      var bounds = launch.getBoundingClientRect();
      if (!(bounds.width > 0 && bounds.height > 0)) { suppress(false); return; }
      var safeStyle = win.getComputedStyle(safe);
      var edge = (options.edge == null ? 16 : options.edge) + (parseFloat(safeStyle.paddingRight) || 0);
      var bottomSafe = parseFloat(safeStyle.paddingBottom) || 0;
      var available = { left: view.left, top: view.top, width: view.width, height: view.height - bottomSafe };
      var position = place({ viewport: available, width: bounds.width, height: bounds.height,
        edge: edge, gap: options.gap, topInset: options.topInset,
        obstacles: obstacles.map(function (element) { return visibleRect(element, view); }).filter(Boolean) });
      if (!position) { suppress(true); return; }
      put('position', 'fixed'); put('left', Math.round(position.left) + 'px'); put('top', Math.round(position.top) + 'px');
      put('right', 'auto'); put('bottom', 'auto'); put('transform', 'none');
      suppress(false);
    }
    function refresh() {
      if (!stopped && frame == null) frame = win.requestAnimationFrame(update);
    }
    var events = ['resize', 'orientationchange', 'scroll', 'pageshow', 'cart:updated', 'cart:refresh'];
    events.forEach(function (name) { win.addEventListener(name, refresh, { passive: true }); });
    var themeEvents = ['shopify:section:load', 'shopify:section:unload', 'transitionend', 'animationend'];
    themeEvents.forEach(function (name) { doc.addEventListener(name, refresh); });
    if (win.visualViewport) {
      win.visualViewport.addEventListener('resize', refresh, { passive: true });
      win.visualViewport.addEventListener('scroll', refresh, { passive: true });
    }
    var mutations = new win.MutationObserver(refresh);
    mutations.observe(doc.documentElement, { subtree: true, childList: true, attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-hidden', 'aria-expanded', 'data-sd-satc-show'] });
    refresh();
    return { refresh: refresh, isSuspended: function () { return suspended; }, destroy: function () {
      if (stopped) return;
      stopped = true;
      if (frame != null) win.cancelAnimationFrame(frame);
      mutations.disconnect(); if (resizeObserver) resizeObserver.disconnect();
      events.forEach(function (name) { win.removeEventListener(name, refresh); });
      themeEvents.forEach(function (name) { doc.removeEventListener(name, refresh); });
      if (win.visualViewport) {
        win.visualViewport.removeEventListener('resize', refresh);
        win.visualViewport.removeEventListener('scroll', refresh);
      }
      styleKeys.forEach(restore); safe.remove();
    } };
  }

  var api = { place: place, mount: mount, obstacleSelectors: OBSTACLES.slice(), modalSelectors: MODALS.slice() };
  if (typeof module === 'object' && module.exports) module.exports = api;
  else scope.SiddabotPlacement = api;
})(typeof window === 'undefined' ? globalThis : window);

/* Submitted questions only. The widget calls enqueue before clearing its composer.
 * After an authoritative hello, bind(context), then flush(). UI close must not
 * destroy this queue. No background retry may change an entry's original context.
 * Server ACK: {accepted:true,durable:true,messageId,context,...job/messages}.
 * Server must durably deduplicate SID/context/messageId and reject changed payloads.
 */
(function (scope) {
  'use strict';
  var SOURCES = ['chat', 'composer', 'quick_reply', 'order_card'];
  function copy(value) { return JSON.parse(JSON.stringify(value)); }

  function create(options) {
    options = options || {};
    if (!options.sid || typeof options.send !== 'function') throw new TypeError('sid and send are required');
    var sid = String(options.sid), key = options.key || 'contact-sdb3:outbox';
    var storage = options.storage, now = options.now || Date.now;
    var uuid = options.uuid || function () { return scope.crypto.randomUUID(); };
    var maxItems = options.maxItems || 50, maxChars = options.maxChars || 500;
    var active = null, savedContext = null, entries = [], epoch = 0, flight = null;
    var storageError = false, restoreError = false;
    function state(type, extra) {
      if (typeof options.onState === 'function') {
        try { options.onState(Object.assign({ type: type, pending: entries.length, bound: !!active }, extra || {})); } catch (_) {}
      }
    }
    function persist() {
      try {
        if (!storage || typeof storage.setItem !== 'function') throw new Error('storage unavailable');
        storage.setItem(key, JSON.stringify({ version: 1, sid: sid, context: savedContext, entries: entries }));
        storageError = false; return true;
      } catch (_) { storageError = true; state('storage_error'); return false; }
    }
    function validEntry(entry, context) {
      return typeof context === 'string' && !!context && entry && entry.sid === sid && entry.context === context &&
        typeof entry.messageId === 'string' && /^[a-zA-Z0-9-]{16,80}$/.test(entry.messageId) &&
        typeof entry.text === 'string' && !!entry.text.trim() && entry.text.length <= maxChars &&
        typeof entry.page === 'string' && entry.page.length <= 200 &&
        typeof entry.submittedAt === 'string' && Number.isFinite(Date.parse(entry.submittedAt)) &&
        SOURCES.includes(entry.source);
    }
    try {
      if (!storage || typeof storage.getItem !== 'function') throw new Error('storage unavailable');
      var raw = storage.getItem(key);
      if (raw) {
        var saved = JSON.parse(raw);
        if (!saved || saved.version !== 1 || !Array.isArray(saved.entries)) throw new Error('invalid queue');
        if (saved.sid !== sid) {
          // A replaced SID must not inherit another session's pending private text.
          state('reset', { reason: 'session_changed', dropped: saved.entries.length });
          persist();
        } else {
          if (saved.context !== null && (typeof saved.context !== 'string' || saved.context.length > 100)) throw new Error('invalid context');
          if (saved.entries.length > maxItems || !saved.entries.every(function (entry) { return validEntry(entry, saved.context); })) throw new Error('invalid queue');
          var seen = new Set();
          if (saved.entries.some(function (entry) { if (seen.has(entry.messageId)) return true; seen.add(entry.messageId); return false; })) throw new Error('duplicate queue IDs');
          savedContext = saved.context; entries = saved.entries.map(copy);
        }
      }
    } catch (_) { restoreError = storageError = true; state('storage_error'); }

    function bind(context) {
      if (typeof context !== 'string' || !context || context.length > 100) throw new TypeError('A confirmed context is required');
      if (active === context) return { pending: entries.length, dropped: 0 };
      epoch++;
      var dropped = 0;
      if (savedContext !== null && savedContext !== context) {
        dropped = entries.length; entries = [];
      }
      savedContext = active = context;
      // Writing the new empty context also verifies persistence before submission.
      if (!restoreError) persist();
      if (dropped) state('reset', { reason: 'context_changed', dropped: dropped });
      else state('ready');
      return { pending: entries.length, dropped: dropped };
    }
    function enqueue(question) {
      question = question || {};
      if (!active) return { ok: false, error: 'context_unconfirmed' };
      var text = typeof question.text === 'string' ? question.text.trim() : '';
      if (!text || text.length > maxChars) return { ok: false, error: 'invalid_text' };
      if (entries.length >= maxItems) { state('queue_full'); return { ok: false, error: 'queue_full' }; }
      // A failed/corrupt restore must never be silently overwritten by new text.
      if (restoreError || (storageError && !persist())) return { ok: false, error: 'storage_unavailable' };
      var messageId;
      try { messageId = uuid(); } catch (_) { return { ok: false, error: 'id_unavailable' }; }
      if (typeof messageId !== 'string' || !/^[a-zA-Z0-9-]{16,80}$/.test(messageId) || entries.some(function (entry) { return entry.messageId === messageId; })) return { ok: false, error: 'id_unavailable' };
      var entry = { messageId: messageId, sid: sid, context: active, text: text,
        page: typeof question.page === 'string' ? question.page.slice(0, 200) : '',
        submittedAt: new Date(now()).toISOString(), source: SOURCES.includes(question.source) ? question.source : 'chat', ui: 'cards' };
      entries.push(entry);
      if (!persist()) { entries.pop(); return { ok: false, error: 'storage_unavailable' }; }
      state('queued', { messageId: messageId });
      return { ok: true, entry: copy(entry) };
    }
    function flush(settings) {
      settings = settings || {};
      if (!active) return Promise.resolve({ sent: 0, pending: entries.length, paused: true });
      if (flight && flight.epoch === epoch) return flight.promise;
      var run = { epoch: epoch, promise: null };
      run.promise = Promise.resolve().then(async function () {
        var sent = 0;
        while (active && run.epoch === epoch && entries.length) {
          var entry = entries[0];
          if (entry.context !== active || entry.sid !== sid) { state('context_error'); break; }
          var response;
          try { response = await options.send(copy(entry), { keepalive: !!settings.keepalive }); }
          catch (_) { state('pending', { messageId: entry.messageId, retryable: true }); break; }
          if (run.epoch !== epoch || !active) break;
          // A job, answer, HTTP200 or ordinary success flag alone is not a receipt.
          if (!response || response.accepted !== true || response.durable !== true ||
              response.messageId !== entry.messageId || response.context !== entry.context) {
            state('pending', { messageId: entry.messageId, retryable: response?.retryable !== false,
              reason: response?.reset ? 'context_reset' : response?.error === 'message_conflict' ? 'message_conflict' : 'unacknowledged' });
            break;
          }
          if (typeof options.beforeAck === 'function') {
            try {
              if (await options.beforeAck(copy(entry), response) === false) { state('receipt_storage_error', { messageId: entry.messageId, retryable: true }); break; }
            } catch (_) { state('receipt_storage_error', { messageId: entry.messageId, retryable: true }); break; }
            if (run.epoch !== epoch || !active) break;
          }
          entries.shift(); sent++;
          // Disk receipt is authoritative even if local cleanup fails. A stale local
          // copy may retry after reload; the server and renderer must deduplicate IDs.
          persist(); state('accepted', { messageId: entry.messageId });
          if (typeof options.onAck === 'function') {
            try { await options.onAck(copy(entry), response); } catch (_) { state('delivery_ui_error', { messageId: entry.messageId }); }
          }
        }
        return { sent: sent, pending: entries.length };
      }).finally(function () { if (flight === run) flight = null; });
      flight = run; return run.promise;
    }
    return {
      bind: bind, enqueue: enqueue, flush: flush,
      pending: function () { return entries.map(copy); },
      isBound: function () { return !!active; },
      pause: function () { active = null; epoch++; state('paused'); },
      clear: function () { var dropped = entries.length; active = savedContext = null; entries = []; restoreError = false; epoch++; persist(); state('reset', { reason: 'cleared', dropped: dropped }); }
    };
  }
  var api = { create: create };
  if (typeof module === 'object' && module.exports) module.exports = api;
  else scope.SiddabotOutbox = api;
})(typeof window === 'undefined' ? globalThis : window);

/* Compact public resources only. This renderer never accepts model HTML or makes
 * requests. Call attach(shadowRoot) once to handle later image failures too. */
(function (scope) {
  'use strict';
  var SHOP_HOSTS = ['siddament.com.au', 'www.siddament.com.au'];
  var PAGE_PATHS = [
    '/pages/contact', '/pages/store-hours', '/pages/faq-and-rules', '/pages/edit-order',
    '/pages/colour-matcher', '/pages/swatch-finder', '/pages/po-orders', '/pages/tds',
    '/pages/slicer-profiles', '/collections/next-restockkkkk', '/account'
  ];
  var bindings = new WeakMap();
  function escape(value) {
    return String(value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function text(value, limit) {
    if (typeof value !== 'string') return '';
    return value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, limit).replace(/[\uD800-\uDBFF]$/, '');
  }
  function publicURL(value) {
    if (typeof value !== 'string' || !value || value.length > 2000 || /[\u0000-\u0020\u007f\\]/.test(value) || !/^https:\/\//i.test(value)) return null;
    var authority = value.slice(8).split(/[/?#]/)[0];
    // Reject input that URL() would silently normalise into an allowed host/path.
    if (/[%@]/.test(authority) || /%(?:2e|2f|5c|00)/i.test(value) || /(?:\/|%2f)\.{1,2}(?:\/|%2f|[?#]|$)/i.test(value)) return null;
    try {
      var url = new URL(value);
      if (url.protocol !== 'https:' || url.username || url.password || url.port) return null;
      return url;
    } catch (_) { return null; }
  }
  function destination(value, kind) {
    var url = publicURL(value);
    if (!url || SHOP_HOSTS.indexOf(url.hostname) < 0 || /[?#]/.test(value)) return null;
    if (kind === 'product') {
      if (!/^\/products\/[a-z0-9][a-z0-9_-]*\/?$/i.test(url.pathname)) return null;
    } else if (kind === 'link') {
      if (PAGE_PATHS.indexOf(url.pathname.replace(/\/$/, '')) < 0) return null;
    } else return null;
    url.hostname = 'siddament.com.au';
    url.pathname = url.pathname.replace(/\/$/, '');
    return { href: url.href, label: url.hostname + url.pathname };
  }
  function imageURL(value) {
    var url = publicURL(value);
    if (!url || value.indexOf('#') >= 0) return '';
    var path;
    try { path = decodeURIComponent(url.pathname); } catch (_) { return ''; }
    if (/[\u0000-\u001f\u007f\\%?#]/.test(path) || /%(?:2f|5c|2e)/i.test(url.pathname) || path.split('/').some(function (part) { return part === '.' || part === '..'; })) return '';
    var permittedPath = url.hostname === 'cdn.shopify.com'
      ? /^\/s\/files\/\d+\/\d+\/\d+\/\d+\/(?:products|files)\/[^/]/.test(path)
      : SHOP_HOSTS.indexOf(url.hostname) >= 0 && /^\/cdn\/shop\/(?:products|files)\/[^/]/.test(path);
    if (!permittedPath || url.search.indexOf('%') >= 0) return '';
    var seen = new Set(), good = true;
    url.searchParams.forEach(function (value, key) {
      if (seen.has(key)) { good = false; return; }
      seen.add(key);
      if (key === 'v') good = good && /^\d{1,20}$/.test(value);
      else if (key === 'width' || key === 'height') good = good && /^\d{1,4}$/.test(value) && Number(value) >= 1 && Number(value) <= 4096;
      else if (key === 'crop') good = good && ['center', 'top', 'bottom', 'left', 'right'].indexOf(value) >= 0;
      else if (key === 'format') good = good && ['jpg', 'jpeg', 'pjpg', 'png', 'webp', 'avif', 'gif'].indexOf(value) >= 0;
      else good = false;
    });
    return good ? url.href : '';
  }
  function html(resources) {
    if (!Array.isArray(resources)) return '';
    var cards = [], products = 0, links = 0, seen = new Set();
    for (var i = 0; i < resources.length && cards.length < 6; i++) {
      var entry = resources[i];
      if (!entry || typeof entry !== 'object' || (entry.kind !== 'product' && entry.kind !== 'link')) continue;
      if ((entry.kind === 'product' && products >= 3) || (entry.kind === 'link' && links >= 3)) continue;
      var target = destination(entry.url, entry.kind), title = text(entry.title, 160);
      if (!target || !title || seen.has(target.href)) continue;
      var image = entry.kind === 'product' ? imageURL(entry.image) : '';
      var price = entry.kind === 'product' ? text(entry.price, 64) : '', caption = text(entry.caption, 200);
      var media = image ? '<span class="sdbc-media"><span class="sdbc-fallback" aria-hidden="true">Image unavailable</span><img data-sdbc-image src="' + escape(image) + '" alt="" loading="lazy" decoding="async" referrerpolicy="no-referrer"></span>' : '';
      var label = title + (price ? ', ' + price : '') + '. ' + (caption ? caption + '. ' : '') + 'Open ' + target.href;
      cards.push('<a class="sdbc-card' + (image ? '' : ' sdbc-card--text') + '" href="' + escape(target.href) + '" aria-label="' + escape(label) + '" target="_blank" rel="noopener noreferrer">' + media +
        '<span class="sdbc-copy"><span class="sdbc-title">' + escape(title) + '</span>' +
        (price ? '<span class="sdbc-price">' + escape(price) + '</span>' : '') +
        (caption ? '<span class="sdbc-caption">' + escape(caption) + '</span>' : '') +
        '<span class="sdbc-url">' + escape(target.label) + '</span><span class="sdbc-go" aria-hidden="true">' + (entry.kind === 'product' ? 'View product' : 'Open page') + '</span></span></a>');
      seen.add(target.href);
      if (entry.kind === 'product') products++; else links++;
    }
    return cards.length ? '<div class="sdbc-cards">' + cards.join('') + '</div>' : '';
  }
  function attach(root) {
    if (!root || typeof root.addEventListener !== 'function') return function () {};
    if (bindings.has(root)) return bindings.get(root);
    function failed(image) {
      if (!image || image.nodeName !== 'IMG' || typeof image.hasAttribute !== 'function' || !image.hasAttribute('data-sdbc-image')) return;
      image.hidden = true;
      var media = typeof image.closest === 'function' ? image.closest('.sdbc-media') : null;
      if (media && media.classList) media.classList.add('sdbc-media--failed');
    }
    function onError(event) { failed(event.target); }
    root.addEventListener('error', onError, true);
    // A cached failed image can have completed before the parent attaches us.
    if (typeof root.querySelectorAll === 'function') {
      Array.prototype.forEach.call(root.querySelectorAll('img[data-sdbc-image]'), function (image) {
        if (image.complete && image.naturalWidth === 0) failed(image);
      });
    }
    var detach = function () { root.removeEventListener('error', onError, true); bindings.delete(root); };
    bindings.set(root, detach);
    return detach;
  }
  var css = '.sdbc-cards{display:grid;gap:8px;margin-top:10px}.sdbc-card{display:grid;grid-template-columns:72px minmax(0,1fr);gap:10px;align-items:start;padding:10px;border:1px solid rgba(255,255,255,.1);border-radius:12px;background:#0a0a0b;color:inherit;text-decoration:none}.sdbc-card:hover{border-color:#3b82f6}.sdbc-card:focus-visible{outline:2px solid #3b82f6;outline-offset:2px}.sdbc-card--text{grid-template-columns:minmax(0,1fr)}.sdbc-media{position:relative;display:grid;place-items:center;width:72px;height:72px;border-radius:8px;overflow:hidden;background:#fff;color:#52525b}.sdbc-fallback{padding:6px;text-align:center;font-size:10px;line-height:1.25}.sdbc-media img{position:absolute;inset:0;display:block;width:100%;height:100%;object-fit:contain;background:#fff}.sdbc-media img[hidden]{display:none}.sdbc-copy{display:grid;min-width:0;gap:3px}.sdbc-title{font-size:13px;line-height:1.4;font-weight:650;color:#fafafa;overflow-wrap:anywhere}.sdbc-price{font-size:13px;line-height:1.35;font-weight:650;color:#fafafa}.sdbc-caption{font-size:12px;line-height:1.4;color:#d4d4d8;overflow-wrap:anywhere}.sdbc-url{font-size:11px;line-height:1.4;color:rgba(255,255,255,.5);overflow-wrap:anywhere;word-break:break-word}@media(max-width:420px){.sdbc-card{grid-template-columns:64px minmax(0,1fr);gap:8px}.sdbc-card--text{grid-template-columns:minmax(0,1fr)}.sdbc-media{width:64px;height:64px}}';
  css += '.b a.sdbc-card{text-decoration:none}.sdbc-go{justify-self:start;margin-top:5px;padding:4px 12px;border:1px solid rgba(255,255,255,.24);border-radius:999px;font-size:12px;line-height:1.2;font-weight:600;color:#fff}.sdbc-card:hover .sdbc-go,.sdbc-card:focus-visible .sdbc-go{background:#3b82f6;border-color:#3b82f6}';
  var api = Object.freeze({ html: html, css: css, attach: attach });
  if (typeof module === 'object' && module.exports) module.exports = api;
  else scope.SiddabotCards = api;
})(typeof window === 'undefined' ? globalThis : window);

/* Siddabot side tab: swipe it into the edge to tuck it away (tap or swipe back to bring it out),
 * a red dot when a reply is waiting, and this browser's own prompt and on/off settings.
 * No network calls. Settings live in localStorage and fail quietly when storage is blocked. */
(function (scope) {
  'use strict';
  var PREFIX = 'siddabot:';

  function prefs(win) {
    var store = null;
    try { store = win.localStorage; } catch (_) { store = null; }
    var memory = {};
    return {
      get: function (key) { try { return store ? store.getItem(PREFIX + key) : (memory[key] || null); } catch (_) { return memory[key] || null; } },
      set: function (key, value) {
        if (value == null) delete memory[key]; else memory[key] = value;
        try { if (store) { if (value == null) store.removeItem(PREFIX + key); else store.setItem(PREFIX + key, value); } } catch (_) {}
      }
    };
  }

  function attach(options) {
    var launch = options.launcher;
    if (!launch || !launch.ownerDocument) throw new TypeError('A launcher element is required');
    var doc = launch.ownerDocument, win = doc.defaultView, store = prefs(win);
    var startX = null, startY = null, pointer = null, dx = 0, dragging = false, swallowClick = false;
    var lastX = 0, lastT = 0, vx = 0;   // finger speed in px/ms, for flicks

    function isTucked() { return store.get('tucked') === '1'; }
    function isOff() { return store.get('off') === '1'; }
    function promptsOn() { return store.get('prompts') !== '0'; }
    function apply() {
      launch.classList.toggle('tucked', isTucked());
      launch.classList.toggle('off', isOff());
      launch.setAttribute('aria-label', isTucked() ? 'Show the Siddabot tab' : 'Ask Siddabot');
      launch.setAttribute('title', isTucked() ? 'Show the Siddabot tab' : 'Ask Siddabot');
    }
    function tuck(value) {
      store.set('tucked', value ? '1' : null); apply();
      if (typeof options.onChange === 'function') options.onChange();
    }

    function onDown(e) {
      if (e.button > 0 || isOff()) return;
      pointer = e.pointerId; startX = e.clientX; startY = e.clientY; dx = 0; dragging = false;
      lastX = e.clientX; lastT = e.timeStamp; vx = 0;
    }
    function onMove(e) {
      if (pointer !== e.pointerId || startX == null) return;
      var x = e.clientX - startX, y = e.clientY - startY;
      var dt = e.timeStamp - lastT;
      if (dt > 0) { vx = vx * 0.4 + ((e.clientX - lastX) / dt) * 0.6; lastX = e.clientX; lastT = e.timeStamp; }
      if (!dragging) {
        if (Math.abs(x) < 4 || Math.abs(x) < Math.abs(y)) return;
        dragging = true;
        try { launch.setPointerCapture(e.pointerId); } catch (_) {}
        launch.classList.add('dragging');
      }
      // Tucked: only a pull back out (left) moves it. Out: only a push towards the edge (right).
      dx = isTucked() ? Math.max(-80, Math.min(0, x)) : Math.max(0, Math.min(80, x));
      launch.style.setProperty('--drag', dx + 'px');
      if (e.cancelable) e.preventDefault();
    }
    function onUp(e) {
      if (pointer !== e.pointerId) return;
      pointer = null; startX = null;
      if (!dragging) return;
      dragging = false; swallowClick = true;
      launch.classList.remove('dragging'); launch.style.removeProperty('--drag');
      // A flick (fast, however short) decides by its direction. Otherwise: the tab is flush with the screen edge,
      // so a finger only has ~10px to travel and a short push or reaching the edge tucks it; a short pull brings it out.
      var flick = Math.abs(vx) > 0.25 && e.timeStamp - lastT < 120;
      if (!isTucked() && (flick ? vx > 0 : dx >= 6 || e.clientX >= doc.documentElement.clientWidth - 2)) tuck(true);
      else if (isTucked() && (flick ? vx < 0 : dx <= -8)) tuck(false);
    }
    // The widget calls this from its click handler; true means the tap was used here, not to open chat.
    function consumeClick() {
      if (swallowClick) { swallowClick = false; return true; }
      // A waiting reply opens the chat straight from the sliver; otherwise a tap just brings the tab back out.
      if (isTucked()) { var waiting = launch.classList.contains('has-reply'); tuck(false); return !waiting; }
      return false;
    }

    function clearNotice() { launch.classList.remove('has-reply'); }
    // A reply finished while the chat was closed: a red dot on the tab, nothing else on screen.
    function notify() {
      if (isOff() || !promptsOn()) return false;
      launch.classList.add('has-reply');
      return true;
    }
    function setPrompts(value) { store.set('prompts', value ? null : '0'); if (!value) clearNotice(); return promptsOn(); }
    function setOff(value) { store.set('off', value ? '1' : null); if (value) { clearNotice(); store.set('tucked', null); } apply(); return isOff(); }

    launch.addEventListener('pointerdown', onDown);
    launch.addEventListener('pointermove', onMove);
    launch.addEventListener('pointerup', onUp);
    launch.addEventListener('pointercancel', onUp);
    apply();
    return { consumeClick: consumeClick, notify: notify, clearNotice: clearNotice, isOff: isOff, setOff: setOff,
      promptsOn: promptsOn, setPrompts: setPrompts, isTucked: isTucked, tuck: tuck };
  }

  var css = '.launch{right:0;top:55%;bottom:auto;display:flex;align-items:center;gap:5px;width:auto;height:auto;min-height:0;padding:9px 3px;' +
    'writing-mode:vertical-rl;border:1px solid rgba(255,255,255,.14);border-right:0;border-radius:9px 0 0 9px;background:#1a1a1d;color:#f4f4f5;box-shadow:0 2px 14px rgba(0,0,0,.55);' +
    'font:600 10px/1 "Instrument Sans",system-ui,sans-serif;letter-spacing:.05em;animation:none;touch-action:pan-y;user-select:none;-webkit-user-select:none;' +
    'translate:var(--drag,0px) 0;transition:translate .28s cubic-bezier(.2,.9,.3,1.15),background-color .15s}' +
    '.launch .lbl{white-space:nowrap}.launch:hover{background:#242428;border-color:rgba(255,255,255,.22);color:#fff}.launch svg{writing-mode:horizontal-tb;flex:none;width:11px;height:11px;opacity:.85}' +
    // The slim tab gets a wider invisible grab area; tucked, the sliver's grab area is wider still.
    '.launch::before{content:"";position:absolute;top:-6px;bottom:-6px;left:-14px;width:14px}' +
    '.launch.dragging{transition:none}.launch.tucked{translate:calc(100% - 7px + var(--drag,0px)) 0}' +
    '.launch.tucked::before{top:-18px;bottom:-18px;left:-30px;width:37px}' +
    '.launch.off{display:none!important}' +
    '.launch .dot{display:none;position:absolute;top:-4px;left:-4px;width:10px;height:10px;border-radius:50%;background:#ef4444;box-shadow:0 0 0 2px #1a1a1d}' +
    '.launch.has-reply .dot{display:block}.launch.tucked.has-reply .dot{top:auto;bottom:50%;left:-12px}' +
    '.hbs{display:flex;align-items:center;gap:2px;margin-left:auto}.hb{display:grid;place-items:center;width:36px;height:36px;border-radius:50%;color:rgba(255,255,255,.6)}' +
    '.hb:hover{color:#fff;background:#27272a}.hb:focus-visible{outline:2px solid #3b82f6;outline-offset:2px}.hb svg{width:18px;height:18px}' +
    '.hb.bell[aria-pressed="true"] .slash{display:none}.hb.bell[aria-pressed="false"]{color:rgba(255,255,255,.35)}' +
    '.offbar{display:grid;gap:10px;padding:12px 16px;border-bottom:1px solid rgba(255,255,255,.1);font-size:14px;line-height:1.4;color:rgba(255,255,255,.85)}' +
    '.offbar[hidden]{display:none}.offbar p{margin:0}.offbar .row{display:flex;gap:8px;flex-wrap:wrap}' +
    '.offbar button{padding:7px 14px;border-radius:999px;border:1px solid rgba(255,255,255,.24);font-weight:600;font-size:13px;color:#fff}' +
    '.offbar .offyes{background:#ef4444;border-color:#ef4444}.offbar button:focus-visible{outline:2px solid #3b82f6;outline-offset:2px}';

  var api = Object.freeze({ attach: attach, css: css });
  if (typeof module === 'object' && module.exports) module.exports = api;
  else scope.SiddabotDock = api;
})(typeof window === 'undefined' ? globalThis : window);

/* Siddabot website chat (Sidd 22/10/2026, "bubbles"). One script, no dependencies, loaded from the theme
   (assets/siddabot.js). Talks to /apps/edit-order/api/bot/* (Shopify app proxy -> sd-webbot).
   Everything lives in a Shadow DOM so the theme's CSS can't reach in (23/10: the theme's .card rules broke the order
   cards). The chat survives page changes in sessionStorage. Every action is a button the customer taps; the server
   checks ownership again before anything runs. */
(function () {
  if (window.__siddabot) return; window.__siddabot = 1;
  var me = document.currentScript, API = '/apps/edit-order/api/bot';
  var externalTrigger = document.getElementById('ct-ai-trigger');
  var generation = 0;
  var S = { get: function (k, d) { try { var v = sessionStorage.getItem('contact-sdb3:' + k); return v ? JSON.parse(v) : d; } catch (e) { return d; } },
            set: function (k, v) { try { sessionStorage.setItem('contact-sdb3:' + k, JSON.stringify(v)); } catch (e) {} } };
  var sid = S.get('sid', null);
  if (!sid) { sid = 'w' + crypto.randomUUID(); S.set('sid', sid); }
  var log = S.get('log', []); // replay only after the server confirms the matching context
  var context = S.get('context', null);

  var CSS = '\
:host{all:initial}\
*{box-sizing:border-box;margin:0}\
button,input,textarea{font:inherit;color:inherit;margin:0}\
button{border:0;background:none;padding:0;cursor:pointer;-webkit-tap-highlight-color:transparent;text-align:left}\
a{color:inherit}\
.root{font:400 15px/1.5 "Instrument Sans",system-ui,-apple-system,"Segoe UI",Arial,sans-serif;color:#fff;-webkit-font-smoothing:antialiased}\
.launch{position:fixed;right:max(16px,env(safe-area-inset-right,0px));bottom:calc(env(safe-area-inset-bottom,0px) + 18px);z-index:3;display:inline-flex;align-items:center;gap:10px;height:50px;padding:0 20px 0 16px;border-radius:999px;background:#fff;color:#000;font-weight:600;font-size:15px;box-shadow:0 6px 24px rgba(0,0,0,.45)}\
.launch:focus-visible,.x:focus-visible,.send:focus-visible,.chip:focus-visible,.go:focus-visible,.ord:focus-visible{outline:2px solid #3b82f6;outline-offset:2px}\
.scrim{position:fixed;inset:0;z-index:1;background:rgba(0,0,0,.6)}\
.sheet{position:fixed;z-index:2;left:0;right:0;bottom:0;height:min(86vh,720px);display:flex;flex-direction:column;background:#0a0a0b;border-top:1px solid rgba(255,255,255,.18);border-radius:18px 18px 0 0;padding-bottom:env(safe-area-inset-bottom,0px);animation:up .26s cubic-bezier(.2,.7,.2,1) both;overflow:hidden}\
@keyframes up{from{transform:translateY(100%)}to{transform:none}}\
@media(min-width:760px){.scrim{background:transparent}.sheet{left:auto;right:24px;bottom:84px;width:392px;height:min(620px,calc(100vh - 110px));border:1px solid rgba(255,255,255,.18);border-radius:18px;box-shadow:0 18px 50px rgba(0,0,0,.6)}.grab{display:none}}\
@media(prefers-reduced-motion:reduce){.sheet{animation:none}}\
.grab{display:block;width:38px;height:4px;margin:8px auto 0;border-radius:2px;background:rgba(255,255,255,.25)}\
.hd{flex:0 0 auto;display:flex;align-items:center;gap:12px;padding:10px 12px 10px 16px;border-bottom:1px solid rgba(255,255,255,.1)}\
.av{flex:0 0 auto;display:grid;place-items:center;width:34px;height:34px;border-radius:50%;background:#fff;color:#000}\
.ti{flex:1;min-width:0}\
.nm{font:600 12px/1.2 Orbitron,"Instrument Sans",sans-serif;letter-spacing:.14em;text-transform:uppercase}\
.st{display:flex;align-items:center;gap:6px;margin-top:2px;font-size:12px;color:rgba(255,255,255,.55)}\
.st i{width:6px;height:6px;border-radius:50%;background:#4ade80}\
.x{display:grid;place-items:center;width:40px;height:40px;border-radius:50%;color:rgba(255,255,255,.7)}\
.x:hover{background:rgba(255,255,255,.08);color:#fff}\
.bd{flex:1;min-height:0;overflow-y:auto;overflow-x:hidden;overscroll-behavior:contain;padding:16px;display:flex;flex-direction:column;gap:10px;scrollbar-width:thin;scrollbar-color:rgba(255,255,255,.2) transparent}\
.row{display:flex;gap:8px;align-items:flex-end;max-width:100%}\
.row.me{justify-content:flex-end}\
.mini{flex:0 0 auto;display:grid;place-items:center;width:24px;height:24px;border-radius:50%;background:#fff;color:#000}\
.b{min-width:0;max-width:82%;padding:9px 13px;border-radius:18px 18px 18px 5px;background:#1c1c20;overflow-wrap:anywhere}\
.row.me .b{max-width:78%;border-radius:18px 18px 5px 18px;background:#3b82f6}\
.b p+p{margin-top:7px}.b ul{margin:5px 0 0;padding-left:18px}.b li+li{margin-top:2px}\
.b a{text-decoration:underline;text-underline-offset:3px}\
.typing{display:inline-flex;gap:5px;padding:5px 2px}.typing i{width:6px;height:6px;border-radius:50%;background:rgba(255,255,255,.55);animation:bl 1s infinite}\
.typing i:nth-child(2){animation-delay:.15s}.typing i:nth-child(3){animation-delay:.3s}@keyframes bl{50%{opacity:.25}}\
.typing-wrap{display:flex;align-items:center;gap:10px}.typing-time{font-size:12px;color:rgba(255,255,255,.55);font-variant-numeric:tabular-nums}\
.aiwarn{margin:0;padding:6px 16px;border-bottom:1px solid rgba(255,255,255,.1);font-size:12px;line-height:1.4;text-align:center;color:rgba(255,255,255,.55)}\
.ords{margin-top:8px;border-radius:12px;overflow:hidden;background:#0a0a0b}\
.ord{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:2px 10px;width:100%;padding:10px 12px;text-align:left}\
.ord+.ord{border-top:1px solid rgba(255,255,255,.08)}\
.ord:hover{background:#141417}\
.ord b{font-weight:600;font-size:15px}\
.ord .s{align-self:center;font-size:12px;color:rgba(255,255,255,.65);white-space:nowrap}\
.ord .s.g{color:#4ade80}\
.ord small{grid-column:1 / -1;font-size:12px;color:rgba(255,255,255,.55);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}\
.acts{display:flex;flex-wrap:wrap;gap:8px;margin-top:9px}\
.go{display:inline-flex;align-items:center;justify-content:center;min-height:40px;padding:0 18px;border-radius:999px;background:#6aa4ff;color:#07111f;font-weight:600;font-size:14px;text-decoration:none!important}\
.go[disabled]{opacity:.5;cursor:default}\
.vf{display:grid;gap:8px;margin-top:9px}\
.vf input,.sum{width:100%;min-height:40px;padding:0 12px;border:1px solid rgba(255,255,255,.18);border-radius:10px;background:#0a0a0b;outline:0;font-size:16px}\
.vf input:focus,.sum:focus{border-color:#3b82f6}\
.sum{min-height:72px;padding:9px 12px;resize:vertical;line-height:1.4}\
.err{color:#f87171;font-size:13px}\
.note{margin-top:5px;font-size:12px;color:rgba(255,255,255,.5)}\
.qrw{margin-top:9px;padding:10px;border-radius:12px;background:#fff;width:max-content;max-width:100%}\
.qr{display:block;width:180px;height:180px}\
.code{margin-top:6px;font:600 16px/1.3 ui-monospace,Consolas,monospace;letter-spacing:.05em}\
.ft{flex:0 0 auto;padding:10px 12px 12px;border-top:1px solid rgba(255,255,255,.1)}\
.chips{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:10px}\n@media(max-width:759px){.chips{flex-wrap:nowrap;overflow-x:auto;scrollbar-width:none;margin:0 -12px 10px;padding:0 12px}.chips::-webkit-scrollbar{display:none}.chip{flex:0 0 auto}}\
.chip{min-height:32px;padding:0 12px;border:1px solid rgba(255,255,255,.22);border-radius:999px;font-size:13px;white-space:nowrap}\
.chip:hover{border-color:#fff}\
.ask{display:flex;align-items:center;gap:8px;padding:4px 4px 4px 16px;border:1px solid rgba(255,255,255,.18);border-radius:999px;background:#141417}\
.ask:focus-within{border-color:#3b82f6}\
.ask input{flex:1;min-width:0;height:40px;border:0;background:transparent;outline:0;font-size:16px}\
.ask input::placeholder{color:rgba(255,255,255,.45)}\
.send{flex:0 0 auto;display:grid;place-items:center;width:40px;height:40px;border-radius:50%;background:#fff;color:#000}\
.send[disabled]{opacity:.4}';

  CSS += '\n.chip{min-height:44px}.x,.send{width:44px;height:44px}.st i{background:#999}.st.ready i{background:#4ade80}.st.unavailable i{background:#fbbf24}';
  CSS += `
.launch{right:12px;top:55%;bottom:auto;display:grid;place-items:center;width:44px;height:44px;min-height:44px;padding:0;gap:0;writing-mode:horizontal-tb;border-radius:50%;border:1px solid #45454a;background:#18181b;color:#d4d4d8;box-shadow:0 2px 8px #0003;animation:none}
.launch:hover{background:#27272a;color:#fff}.launch svg{flex:none;width:20px;height:20px}.launch[hidden]{display:none!important}
.sheet{height:94vh;height:94dvh;max-height:none}.ask{display:block;padding:10px;border-radius:16px}.ask textarea{display:block;width:100%;min-height:78px;max-height:170px;resize:none;overflow-y:auto;border:0;outline:0;background:transparent;font-size:16px;line-height:1.5;padding:2px 4px;color:#fff}.ask textarea::placeholder{color:#a1a1aa}
.composer-controls{display:flex;align-items:center;gap:8px;margin-top:6px}.count{margin-right:auto;color:#a1a1aa;font-size:12px;font-variant-numeric:tabular-nums}.count.full{color:#fbbf24}
.mic{display:grid;place-items:center;flex:none;width:44px;height:44px;border-radius:50%;color:#d4d4d8;background:#27272a}.mic:hover{background:#3f3f46}.mic:focus-visible{outline:2px solid #6aa4ff;outline-offset:2px}.mic[disabled]{opacity:.4;cursor:default}.mic.is-listening{background:#dc2626;color:#fff;box-shadow:0 0 0 3px #dc262633}
.voice-status{font-size:12px;color:#a1a1aa;line-height:1.35;margin:7px 4px 0;min-height:16px}.chips{margin-bottom:8px}.bd{padding:20px;gap:14px}.b{max-width:90%;padding:12px 15px}.root{font-size:16px}.hd{padding:14px 14px 14px 20px}.nm{font-size:13px}.name-line{display:flex;align-items:center;gap:7px;flex-wrap:wrap}.beta{display:inline-block;padding:2px 6px;border:1px solid #45454a;border-radius:999px;background:#202024;color:#d4d4d8;font:500 10px/1.2 "Instrument Sans",system-ui,sans-serif;letter-spacing:.02em}.ft{padding:12px 14px}.scrim{background:rgba(0,0,0,.45)}
@media(min-width:760px){.sheet{right:16px;bottom:16px;width:min(500px,calc(100vw - 32px));height:min(740px,calc(100vh - 32px));height:min(740px,calc(100dvh - 32px));border-radius:20px}.grab{display:none}}
@media(max-width:759px){.bd{padding:14px}.b{max-width:90%}.ft{padding-bottom:12px}.launch{width:44px;height:44px;padding:0}.hd{padding:10px 12px 10px 16px}}
@media(max-height:500px){.sheet{height:100vh;height:100dvh;bottom:0}.chips{display:none}.ask textarea{min-height:46px;max-height:90px}.hd{padding-top:4px;padding-bottom:4px}.voice-status{margin-top:4px}}
.sheet.compact .chips{display:none}.sheet.compact .ask textarea{min-height:46px;max-height:90px}.sheet.compact .hd{padding-top:4px;padding-bottom:4px}.sheet.compact .ft{max-height:calc(100% - 56px);overflow-y:auto}.voice-status:empty{display:none}.delivery-status{font-size:12px;line-height:1.4;color:#d4d4d8;margin:7px 4px 0}.delivery-status:empty{display:none}
`;
  CSS += window.SiddabotCards.css;CSS += window.SiddabotDock.css;CSS += ".staff-name{display:block;margin-bottom:3px;font-size:12px;font-weight:700;color:#6aa4ff}";
  var ring = function (s) { return '<svg width="' + s + '" height="' + s + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="8.5"/><circle cx="12" cy="12" r="3"/></svg>'; };
  var esc = function (s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); };
  // Bot text -> safe HTML: escape everything, then link our own URLs and keep paragraphs / bullets.
  function fmt(t) {
    var h=esc(t).replace(/\[([^\]\n]+)\]\((https:\/\/[^\s)]+)\)/g,'$2').replace(/\*\*([^*\n]+)\*\*/g,'<b>$1</b>');
    h=h.replace(/https:\/\/[^\s<)]+/g,function(raw){
      var tail='',u=raw;while(/[.,!?;:]$/.test(u)){tail=u.slice(-1)+tail;u=u.slice(0,-1);}
      try{var parsed=new URL(u.replace(/&amp;/g,'&'));if(parsed.protocol!=='https:'||!['siddament.com.au','www.siddament.com.au'].includes(parsed.hostname))return raw;
        return '<a href="'+esc(parsed.href)+'" target="_blank" rel="noopener">'+esc(parsed.hostname+parsed.pathname+parsed.search)+'</a>'+tail;
      }catch(e){return raw;}
    });
    return h.split(/\n{2,}/).map(function(p){var lines=p.split('\n'),bullet=/^\s*[-•*]\s+/;if(lines.every(function(l){return bullet.test(l);}))return '<ul>'+lines.map(function(l){return '<li>'+l.replace(bullet,'')+'</li>';}).join('')+'</ul>';return '<p>'+lines.map(function(l){return l.replace(bullet,'• ');}).join('<br>')+'</p>';}).join('');
  }
  function resetTranscript(fresh){if(voice)voice.abort('');resetDelivery();context=fresh;S.set('context',context);log=[];S.set('log',log);generation++;if(body)body.replaceChildren();}
  function unreachable(){return {unavailable:true,messages:[{text:'Siddabot is unavailable right now. Use Raise a ticket or email support@siddament.com.au.'}]};}
  function post(path,payload){
    payload=payload||{};payload.sid=sid;if(path!=='hello'&&context)payload.context=context;
    var startedGeneration=generation;
    return fetch(API+'/'+path,{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)})
      .then(function(r){if(!r.ok)throw new Error('unavailable');return r.json();})
      .then(function(r){if(startedGeneration!==generation)return {stale:true,messages:[]};if(r.error)return unreachable();if(r.reset){resetTranscript(r.context);r.text='Your sign-in changed. Please ask your question again.';}if(r.ok&&r.verifiedContext&&r.verifiedContext!==context){resetTranscript(r.verifiedContext);reconnectQuestions();}return r;})
      .catch(function(){return startedGeneration===generation?unreachable():{stale:true,messages:[]};});
  }
  // Answers run in the background on the server; poll until ready (gives up after ~90 s).
  function settle(r,preservePending) {
    if (!r || !r.job) return Promise.resolve(r);
    var tries = 0, answerGeneration = generation;
    return new Promise(function (done) {
      (function tick() {
        setTimeout(function () {
          if(answerGeneration!==generation){done({stale:true,messages:[]});return;}
          post('poll', { job: r.job }).then(function (p) {
            if(p.stale||answerGeneration!==generation){done({stale:true,messages:[]});return;}
            if(preservePending&&p.unavailable){done({pending:true});return;}
            if (p.done || ++tries > 60) done(p.done ? p : preservePending?{pending:true}:{ messages: [{ text: 'That took too long. Try again, or contact us at https://siddament.com.au/pages/contact' }] });
            else tick();
          });
        }, tries < 4 ? 900 : 1500);
      })();
    });
  }

  var host = document.createElement('div'); host.id = 'siddabot';
  // !important: the theme hides empty divs (div:empty{display:none}) and the host has no light-DOM children.
  host.style.cssText = 'display:block!important;position:fixed!important;top:0;left:0;width:0;height:0;z-index:2147483000!important;visibility:visible!important';
  document.body.appendChild(host);
  var shadow = host.attachShadow({ mode: 'open' });window.SiddabotCards.attach(shadow);
  shadow.innerHTML = '<style>' + CSS + '</style><div class="root"><button class="launch" type="button" aria-haspopup="dialog">' +
    '<svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 5h16v11H9l-5 4z"/></svg><span class="lbl">Ask Siddabot</span><span class="dot" aria-hidden="true"></span></button></div>';
  var root = shadow.querySelector('.root'), launch = shadow.querySelector('.launch');
  var sheet, body, scrim, busy = false, input, sendBtn, voice, placement, lastOpener, dock;

  function add(who, html, save, messageId) {
    var known=messageId&&log.some(function(m){return m.id===messageId;});
    if(messageId&&body){var existing=Array.from(body.children).find(function(n){return n.dataset.messageId===messageId;});if(existing)return existing;}
    var d = document.createElement('div'); d.className = 'row ' + who;
    d.innerHTML = who === 'bot' ? '<span class="mini">' + ring(13) + '</span><div class="b">' + html + '</div>' : '<div class="b">' + html + '</div>';
    if(messageId)d.dataset.messageId=messageId;body.appendChild(d); body.scrollTop = body.scrollHeight;
    if (save !== false&&!known) { log.push({ who: who, html: html, id:messageId||null }); if (log.length > 60) log = log.slice(-60); S.set('log', log); }
    return d;
  }
  // Sidd 29/10/2026: "show a time to reply". Replies take about 10-30 s, so the bubble counts the seconds.
  function typing() {
    var d = add('bot', '<span class="typing-wrap"><span class="typing" aria-label="Siddabot is typing"><i></i><i></i><i></i></span><span class="typing-time" aria-hidden="true">Replying… 0s</span></span>', false);
    var started = Date.now(), el = d.querySelector('.typing-time');
    var timer = setInterval(function () { if (!d.isConnected) { clearInterval(timer); return; } el.textContent = 'Replying… ' + Math.round((Date.now() - started) / 1000) + 's'; }, 1000);
    return d;
  }
  function render(m,messageId) {
    var h = fmt(m.text || '');
    if(Array.isArray(m.resources))h+=window.SiddabotCards.html(m.resources);
    if (m.cards && m.cards.length) h += '<div class="ords">' + m.cards.map(function (c) {
      return '<button type="button" class="ord" data-ask="Order ' + esc(c.name) + '"><b>' + esc(c.name) + '</b><span class="s' + (c.ok ? ' g' : '') + '">' + esc(c.status) + '</span><small>' + esc(c.date) + ' · ' + esc(c.items) + '</small></button>';
    }).join('') + '</div>';
    if (m.offers && m.offers.length) h += m.offers.map(function (o) {
      return o.kind === 'team' ? '<div class="team"><textarea class="sum" aria-label="What to tell the team">' + esc(o.summary || '') + '</textarea><div class="note">You can edit this before it goes.</div><div class="acts"><button class="go" data-tok="' + esc(o.token) + '" data-team="1">' + esc(o.label) + '</button></div></div>'
        : '<div class="acts"><button class="go" data-tok="' + esc(o.token) + '">' + esc(o.label) + '</button></div>';
    }).join('');
    if (m.verify) h += '<form class="vf"><input name="order" placeholder="Order number, e.g. 43288" inputmode="numeric" autocomplete="off" required><input name="email" type="email" placeholder="Email you ordered with" autocomplete="email" required><div class="acts"><button class="go" type="submit">Find my order</button></div><div class="err" hidden></div></form>';
    add('bot', h,true,messageId);
  }
  function setBusy(b) { busy = b; if (sendBtn) sendBtn.disabled = b; if(voice)voice.setEnabled(!b); }

  // BEGIN SIDDABOT QUESTION DELIVERY
  function assistantStatus(r){return r.signedIn===true?'Signed in · AI assistant':'AI assistant · online';}
  function welcomeMessage(r){return r.signedIn===true?"You're signed in. I can look up your own recent orders and account details when you ask. What can I help with?":'Hey, what can I help with? Ask about products, shipping or the shop. I can also help with your own orders once you verify them.';}
  var questionReplies = S.get('replies', []);
  if(!Array.isArray(questionReplies))questionReplies=[];
  questionReplies=questionReplies.filter(function(e){return e&&e.sid===sid&&typeof e.messageId==='string'&&typeof e.context==='string'&&typeof e.text==='string'&&e.text.length<=500;});
  var confirmedContext=null,deliveryNote='',retryTimer=null,retryCount=0,deliveryEpoch=0,helloFlight=null,replyFlights=new Map();
  function deliveryStatus(text){deliveryNote=text||'';if(sheet){var el=sheet.querySelector('.delivery-status');if(el)el.textContent=deliveryNote;}}
  function writeDelivery(key,value){try{sessionStorage.setItem('contact-sdb3:'+key,JSON.stringify(value));return true;}catch(e){return false;}}
  function resetDelivery(){
    deliveryEpoch++;confirmedContext=null;helloFlight=null;replyFlights=new Map();
    if(retryTimer){clearTimeout(retryTimer);retryTimer=null;}retryCount=0;
    questionReplies=[];writeDelivery('replies',questionReplies);
    if(questionOutbox)questionOutbox.clear();
  }
  function scheduleQuestionRetry(){
    if(retryTimer||(!confirmedContext&&!questionReplies.length&&!(questionOutbox&&questionOutbox.pending().length)))return;
    retryTimer=setTimeout(function(){retryTimer=null;if(!document.hidden)reconnectQuestions();},Math.min(30000,2000*Math.pow(2,Math.min(retryCount++,4))));
  }
  function queueReply(entry){
    if(entry.context!==context||entry.context!==confirmedContext)return false;
    var next=questionReplies.filter(function(e){return e.messageId!==entry.messageId;});next.push(entry);
    if(!writeDelivery('replies',next)){deliveryStatus('Your message is saved on our server. This browser could not save reply progress; keep this tab open.');scheduleQuestionRetry();return false;}
    questionReplies=next;return true;
  }
  function contextChanged(fresh){
    resetTranscript(fresh);questionOutbox.pause();confirmedContext=null;
    if(input)input.disabled=true;if(sheet){setBusy(true);sheet.querySelectorAll('.chip').forEach(function(b){b.disabled=true;});}
    deliveryStatus('Your sign-in changed. Close and reopen chat before sending again. Pending questions from the earlier sign-in were not replayed.');
  }
  function rawQuestion(entry,settings){
    var requestEpoch=deliveryEpoch,controller=new AbortController();
    var timeout=setTimeout(function(){controller.abort();},10000);
    return fetch(API+'/chat',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify(entry),keepalive:!!(settings&&settings.keepalive),signal:controller.signal})
      .then(function(r){return r.json().then(function(data){if(!r.ok&&r.status!==503)throw new Error('delivery_failed');return data;});})
      .then(function(r){
        if(requestEpoch!==deliveryEpoch)return {stale:true};
        if(r.reset||(r.accepted===true&&typeof r.context==='string'&&r.context!==entry.context)){contextChanged(r.context);return {stale:true,reset:true};}
        return r;
      }).finally(function(){clearTimeout(timeout);});
  }
  function finishReply(entry){
    if(!writeDelivery('log',log))return false;
    var next=questionReplies.filter(function(e){return e.messageId!==entry.messageId;});
    if(!writeDelivery('replies',next))return false;
    questionReplies=next;return true;
  }
  function deliverReceipt(entry,response){
    if(replyFlights.has(entry.messageId))return replyFlights.get(entry.messageId).then(function(){
      // A reopened view may have joined a poll started by the closed view. Once
      // that old poll retires, recover by submission ID rather than a consumed job.
      if(sheet&&entry.context===context&&entry.context===confirmedContext&&questionReplies.some(function(e){return e.messageId===entry.messageId;})){
        return rawQuestion(entry,{}).then(function(r){if(!r.stale)return deliverReceipt(entry,r);}).catch(function(){scheduleQuestionRetry();});
      }
    });
    if(!questionReplies.some(function(e){return e.messageId===entry.messageId;})&&log.some(function(m){return m.id===entry.messageId+':a:0';}))return Promise.resolve();
    if(!sheet||entry.context!==context||entry.context!==confirmedContext)return Promise.resolve();
    var viewGeneration=generation,viewContext=context,epoch=deliveryEpoch;
    add('me',esc(entry.text),true,entry.messageId+':q');
    var t=typing();setBusy(true);deliveryStatus('Message saved. Waiting for the reply…');
    var pending=settle(response,true).then(function(r){
      if(r.stale||!sheet||viewGeneration!==generation||epoch!==deliveryEpoch||viewContext!==context||entry.context!==confirmedContext)return;
      t.remove();
      if(r.pending||r.unavailable||!Array.isArray(r.messages)){
        deliveryStatus('Your question is saved. Reconnecting to get the reply…');setBusy(false);scheduleQuestionRetry();return;
      }
      r.messages.forEach(function(m,index){render(m,entry.messageId+':a:'+index);});
      if(finishReply(entry)){retryCount=0;deliveryStatus('');}else{deliveryStatus('Reply received. This browser could not save reply progress; keep this tab open.');scheduleQuestionRetry();}
      if(r.off){if(input)input.disabled=true;setBusy(true);}else setBusy(false);
    }).catch(function(){
      if(sheet&&viewGeneration===generation&&entry.context===context){t.remove();setBusy(false);deliveryStatus('Your question is saved. Reconnecting to get the reply…');scheduleQuestionRetry();}
    }).finally(function(){replyFlights.delete(entry.messageId);});
    replyFlights.set(entry.messageId,pending);return pending;
  }
  var questionStorage;try{questionStorage=sessionStorage;}catch(e){questionStorage=null;}
  var questionOutbox=window.SiddabotOutbox.create({storage:questionStorage,key:'contact-sdb3:outbox',sid:sid,send:rawQuestion,beforeAck:queueReply,onAck:deliverReceipt,onState:function(s){
    if(s.type==='receipt_storage_error'){
      deliveryStatus('Your message is saved on our server. This browser could not save reply progress; keep this tab open.');scheduleQuestionRetry();
    }else if(s.type==='pending'){
      deliveryStatus(s.reason==='message_conflict'?'That saved message needs a new submission.':'Message queued. It has not been confirmed saved yet; keep this tab open while we retry.');
      if(sheet&&confirmedContext===context)setBusy(false);scheduleQuestionRetry();
    }else if(s.type==='storage_error')deliveryStatus('This browser could not save delivery progress. Keep this tab open and try again.');
    else if(s.type==='reset'&&s.dropped)deliveryStatus('Pending questions from the earlier sign-in were not replayed.');
  }});
  function resumeReplies(){
    var pendingIds=new Set(questionOutbox.pending().map(function(e){return e.messageId;}));
    return questionReplies.slice().reduce(function(chain,entry){return chain.then(function(){
      if(!sheet||entry.context!==context||entry.context!==confirmedContext||pendingIds.has(entry.messageId))return;
      return rawQuestion(entry,{}).then(function(r){
        if(r.stale)return;
        if(r.accepted!==true||r.durable!==true||r.messageId!==entry.messageId||r.context!==entry.context)throw new Error('unacknowledged');
        return deliverReceipt(entry,r);
      }).catch(function(){deliveryStatus('Your question is saved. Reconnecting to get the reply…');scheduleQuestionRetry();});
    });},Promise.resolve());
  }
  function flushQuestions(settings){
    if(!confirmedContext||confirmedContext!==context||!questionOutbox.isBound())return Promise.resolve();
    if(navigator.onLine===false){deliveryStatus('Message queued offline. Keep this tab open; it will retry when you reconnect.');scheduleQuestionRetry();return Promise.resolve();}
    return resumeReplies().then(function(){return questionOutbox.flush(settings);});
  }
  function helloQuestions(){
    if(helloFlight)return helloFlight;
    var epoch=deliveryEpoch,controller=new AbortController(),timeout=setTimeout(function(){controller.abort();},10000);
    var request=fetch(API+'/hello',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify({sid:sid}),signal:controller.signal})
      .then(function(r){if(!r.ok)throw new Error('unavailable');return r.json();})
      .then(function(r){return epoch===deliveryEpoch?r:{stale:true};})
      .catch(function(){return {unavailable:true};})
      .finally(function(){clearTimeout(timeout);if(helloFlight===request)helloFlight=null;});
    helloFlight=request;return request;
  }
  function bindQuestionContext(r){
    if(context!==r.context)resetTranscript(r.context);
    confirmedContext=r.context;questionOutbox.bind(r.context);
  }
  function reconnectQuestions(){
    if(navigator.onLine===false){scheduleQuestionRetry();return Promise.resolve();}
    return helloQuestions().then(function(r){
      if(r.stale)return;
      if(!r.context||r.unavailable){scheduleQuestionRetry();return;}
      if(context!==r.context){contextChanged(r.context);return;}
      bindQuestionContext(r);if(!sheet)setTimeout(watchReplies,0);
      if(sheet&&!r.off){input.disabled=false;if(!replyFlights.size)setBusy(false);sheet.querySelectorAll('.chip').forEach(function(b){b.disabled=false;});sheet.querySelector('.st').classList.remove('unavailable');sheet.querySelector('.st').classList.add('ready');sheet.querySelector('.who').textContent=assistantStatus(r);}
      return flushQuestions();
    });
  }
  var watchTimer=null,watchJob=null,watchFor=null,watchTries=0;
  function watchReplies(){
    if(watchTimer||sheet||!dock||dock.isOff()||!dock.promptsOn()||!questionReplies.length||!confirmedContext||confirmedContext!==context)return;
    var entry=questionReplies[0];
    if(watchFor!==entry.messageId){watchFor=entry.messageId;watchJob=null;watchTries=0;}
    if(watchTries>=40)return;
    watchTimer=setTimeout(function(){
      watchTimer=null;if(sheet||entry.context!==context)return;watchTries++;
      var check=watchJob?post('poll',{job:watchJob}):rawQuestion(entry,{});
      check.then(function(r){
        if(sheet||!r||r.stale)return;
        if(r.job)watchJob=r.job;
        if(r.done!==false&&!r.job&&Array.isArray(r.messages)&&r.messages.length){dock.notify();return;}
        watchReplies();
      }).catch(function(){watchReplies();});
    },watchTries?5000:1500);
  }
  function ask(text,source){
    if(busy||!text||!sheet)return false;
    if(text.length>500){if(input)input.focus();return false;}
    if(!confirmedContext||confirmedContext!==context){deliveryStatus('Reconnect to chat before sending. Your text is still in the box.');reconnectQuestions();return false;}
    var result=questionOutbox.enqueue({text:text,page:location.pathname,source:source||'quick_reply'});
    if(!result.ok){deliveryStatus(result.error==='queue_full'?'Your delivery queue is full. Keep this tab open while it reconnects; your text is still in the box.':'This browser could not save your message. Your text is still in the box.');return false;}
    if(voice)voice.abort('');setBusy(true);add('me',esc(result.entry.text),true,result.entry.messageId+':q');
    deliveryStatus('Message queued. Saving it…');flushQuestions();return true;
  }
  window.addEventListener('online',function(){reconnectQuestions();});
  window.addEventListener('pageshow',function(){if(questionOutbox.pending().length||questionReplies.length)reconnectQuestions();});
  window.addEventListener('pagehide',function(){if(confirmedContext===context&&questionOutbox.isBound())questionOutbox.flush({keepalive:true});});
  // END SIDDABOT QUESTION DELIVERY
  var staffTimer=null,staffAfter=S.get('staffAfter',0),staffCtx=S.get('staffCtx',null),lastAsk=S.get('lastAsk',0);
  function staffInbox(){
    clearTimeout(staffTimer);staffTimer=null;
    if(document.hidden||!lastAsk||Date.now()-lastAsk>30*60000||(dock&&dock.isOff()))return;
    staffTimer=setTimeout(function(){
      staffTimer=null;if(document.hidden||!context){staffInbox();return;}
      // A new chat context (sign-in change, bot restart) numbers Sidd's messages from 1 again.
      if(staffCtx!==context){staffCtx=context;staffAfter=0;S.set('staffCtx',staffCtx);S.set('staffAfter',0);}
      var asked=context;
      fetch(API+'/inbox',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify({sid:sid,context:asked,after:staffAfter})})
        .then(function(r){return r.ok?r.json():null;})
        .then(function(r){
          if(!r||r.stale||r.context!==asked||asked!==context||!Array.isArray(r.messages))return;
          r.messages.forEach(function(m){
            if(!m||typeof m.n!=='number'||m.n<=staffAfter||typeof m.text!=='string')return;
            staffAfter=m.n;S.set('staffAfter',staffAfter);
            var html='<b class="staff-name">Sidd · SIDDAMENT</b>'+fmt(m.text.slice(0,1000));
            if(sheet)add('bot',html,true,'staff:'+m.n);
            else{log.push({who:'bot',html:html,id:'staff:'+m.n});if(log.length>60)log=log.slice(-60);S.set('log',log);if(dock)dock.notify();}
          });
        }).catch(function(){}).then(staffInbox);
    },sheet?8000:15000);
  }
  var askCore=ask;ask=function(text,source){var sent=askCore(text,source);if(sent){lastAsk=Date.now();S.set('lastAsk',lastAsk);staffInbox();}return sent;};
  staffInbox();

  function fitPanel(){
    if(!sheet)return;var vv=window.visualViewport;var w=vv?vv.width:document.documentElement.clientWidth;var h=vv?vv.height:window.innerHeight;
    var x=vv?vv.offsetLeft:0,y=vv?vv.offsetTop:0;var mobile=w<760;var edge=mobile?0:16;
    sheet.style.height=Math.max(0,Math.min(mobile?h*.94:740,h-edge*2))+'px';
    sheet.style.bottom=Math.max(0,window.innerHeight-y-h)+edge+'px';
    sheet.style.left=mobile?x+'px':'auto';sheet.style.right=mobile?'auto':Math.max(0,document.documentElement.clientWidth-x-w)+edge+'px';
    sheet.style.width=mobile?w+'px':Math.max(0,Math.min(500,w-32))+'px';sheet.classList.toggle('compact',h<500);
    var ta=sheet.querySelector('.ask textarea');if(ta){ta.style.height='auto';ta.style.height=Math.min(h<500?90:170,Math.max(h<500?46:78,ta.scrollHeight))+'px';}
  }
  function open() {
    if (sheet||(placement&&placement.isSuspended())) return; setTimeout(staffInbox,0); if(dock){if(dock.isOff())dock.setOff(false);dock.clearNotice();}clearTimeout(watchTimer);watchTimer=null; document.documentElement.classList.add('sd-siddabot-open');lastOpener=document.activeElement===externalTrigger?externalTrigger:launch; generation++; if(externalTrigger)externalTrigger.setAttribute('aria-expanded','true');
    scrim = document.createElement('div'); scrim.className = 'scrim';
    sheet = document.createElement('section'); sheet.className = 'sheet'; sheet.setAttribute('role', 'dialog'); sheet.setAttribute('aria-label', 'Siddabot chat'); sheet.setAttribute('aria-modal','true');
    sheet.innerHTML = '<span class="grab" aria-hidden="true"></span><div class="hd"><span class="av">' + ring(18) + '</span><div class="ti"><div class="name-line"><div class="nm">Siddabot</div><span class="beta" aria-label="Beta version">Beta</span></div><div class="st" role="status" aria-live="polite" aria-atomic="true"><i aria-hidden="true"></i><span class="who">AI assistant · connecting…</span></div></div><div class="hbs"><button class="hb bell" type="button" aria-pressed="true" aria-label="Reply prompts on"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/><path class="slash" d="M3 3l18 18"/></svg></button><button class="hb pwr" type="button" aria-label="Turn Siddabot off" title="Turn Siddabot off"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M12 2v10"/><path d="M18.4 6.6a9 9 0 1 1-12.8 0"/></svg></button></div><button class="x" type="button" aria-label="Close chat"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div><div class="offbar" hidden><p>Turn Siddabot off? Its tab goes away on every page in this browser. Talk to AI on the contact page turns it back on.</p><div class="row"><button class="offyes" type="button">Turn off</button><button class="offno" type="button">Keep it on</button></div></div>' +
      '<p class="aiwarn">AI can make mistakes</p><div class="bd" aria-live="polite"></div><div class="ft"><div class="chips">' +
      ['Where\'s my order?', 'Get my invoice', 'Pickup code', 'Change my order', 'Is it in stock?', 'Talk to a person'].map(function (c) { return '<button type="button" class="chip">' + c + '</button>'; }).join('') +
      '</div><form class="ask"><textarea placeholder="Type your message…" rows="3" autocomplete="off" aria-label="Message" aria-describedby="sdb-count" maxlength="500"></textarea><div class="composer-controls"><span class="count" id="sdb-count">0 / 500</span><button class="mic" type="button" aria-label="Start voice input" aria-pressed="false" disabled><svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><rect x="9" y="3" width="6" height="12" rx="3"/><path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3M8 22h8"/></svg></button><button class="send" type="submit" aria-label="Send"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M5 12h14M13 6l6 6-6 6"/></svg></button></div></form><p class="voice-status" role="status"></p><p class="delivery-status" role="status"></p></div>';
    root.appendChild(scrim); root.appendChild(sheet);
    fitPanel();window.addEventListener('resize',fitPanel);
    if(window.visualViewport){window.visualViewport.addEventListener('resize',fitPanel);window.visualViewport.addEventListener('scroll',fitPanel);}
    body = sheet.querySelector('.bd'); input = sheet.querySelector('.ask textarea'); sendBtn = sheet.querySelector('.send');
    function updateComposer(){
      if(input.value.length>500)input.value=input.value.slice(0,500);
      var count=sheet.querySelector('.count');count.textContent=input.value.length+' / 500';count.classList.toggle('full',input.value.length>=500);
      var compact=sheet.classList.contains('compact');input.style.height='auto';input.style.height=Math.min(compact?90:170,Math.max(compact?46:78,input.scrollHeight))+'px';
    }
    input.addEventListener('input',updateComposer);
    input.addEventListener('keydown',function(e){if(e.key==='Enter'&&!e.shiftKey&&!e.isComposing&&!window.matchMedia('(pointer:coarse)').matches){e.preventDefault();sheet.querySelector('.ask').requestSubmit();}});
    voice=window.SiddabotVoice.attach({input:input,button:sheet.querySelector('.mic'),status:sheet.querySelector('.voice-status'),form:sheet.querySelector('.ask')});
    updateComposer();
    (function(){var bell=sheet.querySelector('.bell'),pwr=sheet.querySelector('.pwr'),bar=sheet.querySelector('.offbar');
      function syncBell(){var on=dock.promptsOn();bell.setAttribute('aria-pressed',on?'true':'false');bell.setAttribute('title',on?'Reply prompts on':'Reply prompts off');bell.setAttribute('aria-label',on?'Reply prompts on':'Reply prompts off');}
      syncBell();bell.onclick=function(){dock.setPrompts(!dock.promptsOn());syncBell();};
      pwr.onclick=function(){bar.hidden=!bar.hidden;if(!bar.hidden)bar.querySelector('.offno').focus();};
      bar.querySelector('.offno').onclick=function(){bar.hidden=true;pwr.focus();};
      bar.querySelector('.offyes').onclick=function(){dock.setOff(true);close(false);};
    })();
    scrim.onclick = close; sheet.querySelector('.x').onclick = close;
    document.addEventListener('keydown', onKey);
    setBusy(true);input.disabled=true;
    var openingGeneration=generation;helloQuestions().then(function(r){
      if(r.stale||!sheet||openingGeneration!==generation)return;
      if(r.context)bindQuestionContext(r);
      if(r.off||r.unavailable||!r.context){
        sheet.querySelector('.st').classList.add('unavailable');sheet.querySelector('.who').textContent='AI assistant · unavailable';
        add('bot','<p>Siddabot is unavailable right now. The team can help.</p><div class="acts"><a class="go" href="https://siddament.atlassian.net/servicedesk/customer/portals" target="_blank" rel="noopener">Raise a ticket</a></div>',false);
        sheet.querySelectorAll('.chip').forEach(function(b){b.disabled=true;});deliveryStatus(deliveryNote);if(r.context)flushQuestions();else scheduleQuestionRetry();return;
      }
      if(log.length)log.forEach(function(m){add(m.who,m.html,false,m.id);});
      else add('bot',fmt(welcomeMessage(r)));
      sheet.querySelector('.st').classList.add('ready');sheet.querySelector('.who').textContent=assistantStatus(r);
      input.disabled=false;setBusy(false);deliveryStatus(deliveryNote);flushQuestions();
    });
    sheet.querySelector('.chips').onclick = function (e) { var c = e.target.closest('.chip'); if (c) ask(c.textContent); };
    sheet.querySelector('.ask').onsubmit = function (e) { e.preventDefault(); var v = input.value.trim(); if (!v || busy) return; if(ask(v,'composer')){input.value = ''; updateComposer();} };
    sheet.addEventListener('click', function (e) {
      var o = e.target.closest('.ord'); if (o) { ask(o.dataset.ask,'order_card'); return; }
      var b = e.target.closest('button[data-tok]'); if (!b || b.disabled) return;
      if(voice)voice.abort('');b.disabled = true; var lbl = b.textContent; b.textContent = 'One sec…';
      var extra = {}; if (b.dataset.team) { var ta = b.closest('.team').querySelector('textarea'); extra.summary = ta.value; ta.readOnly = true; }
      var t = typing();
      post('act', Object.assign({ token: b.dataset.tok }, extra)).then(function (r) { if(r.stale||!sheet)return;
        t.remove(); b.textContent = r.redirectOnly ? 'Support form ready' : r.ok ? 'Done' : lbl; if (!r.ok) b.disabled = false;
        var h = fmt(r.text || '');
        if (r.link) h += '<div class="acts"><a class="go" href="' + esc(r.link.url) + '" target="_blank" rel="noopener">' + esc(r.link.label) + '</a></div>';
        if (r.qr) h += '<div class="qrw"><img class="qr" alt="Locker QR code" src="' + esc(r.qr) + '"></div><div class="code">' + esc(r.code) + '</div>';
        add('bot', h);
      });
    });
    sheet.addEventListener('submit', function (e) {
      var f = e.target.closest('form.vf'); if (!f) return; e.preventDefault();if(voice)voice.abort('');
      var btn = f.querySelector('button'), err = f.querySelector('.err'); btn.disabled = true; err.hidden = true;
      var t = typing();
      post('verify', { order: f.order.value, email: f.email.value }).then(function (r) { if(r.stale||!sheet)return;
        if (!r.ok) { t.remove(); btn.disabled = false; err.textContent = r.text; err.hidden = false; return; }
        f.querySelectorAll('input').forEach(function (i) { i.disabled = true; }); btn.textContent = 'Found it';
        settle(r).then(function (m) { if(m.stale||!sheet)return; t.remove(); (m.messages || []).forEach(render); });
      });
    });
    launch.hidden = true; launch.style.display = 'none';
    sheet.querySelector('.x').focus();
    S.set('open', 1);
  }
  function close(restoreFocus){if(!sheet)return;document.documentElement.classList.remove('sd-siddabot-open');generation++;busy=false;window.removeEventListener('resize',fitPanel);if(window.visualViewport){window.visualViewport.removeEventListener('resize',fitPanel);window.visualViewport.removeEventListener('scroll',fitPanel);}if(voice){voice.destroy();voice=null;}sheet.remove();scrim.remove();sheet=scrim=null;document.removeEventListener('keydown',onKey);S.set('open',0);launch.hidden=false;if(externalTrigger){externalTrigger.setAttribute('aria-expanded','false');}else{launch.style.display='';}if(placement)placement.refresh();if(restoreFocus!==false&&!document.hidden&&lastOpener&&lastOpener.isConnected)lastOpener.focus();setTimeout(watchReplies,0);}
  function onKey(e){if(e.key==='Escape'){close();return;}if(e.key==='Tab'&&sheet){var all=Array.from(sheet.querySelectorAll('button:not([disabled]),input:not([disabled]),textarea:not([disabled]),a[href]')).filter(function(n){return n.getClientRects().length;});if(!all.length)return;var first=all[0],last=all[all.length-1],active=shadow.activeElement;if(e.shiftKey&&active===first){e.preventDefault();last.focus();}else if(!e.shiftKey&&active===last){e.preventDefault();first.focus();}}}
  launch.setAttribute('aria-label','Ask Siddabot');launch.setAttribute('title','Ask Siddabot');dock=window.SiddabotDock.attach({launcher:launch,onChange:function(){if(placement)placement.refresh();}});launch.onclick=function(){if(dock.consumeClick())return;open();};
  var floatingStyle=document.createElement('style');floatingStyle.textContent='html.sd-siddabot-open #sbPill,html.sd-siddabot-open #effectiveAppsScrollToTopBtn{visibility:hidden!important;pointer-events:none!important}';document.head.appendChild(floatingStyle);
  window.Siddabot=Object.freeze({open:open,close:close,version:'2026-10-29.1'});
  document.addEventListener('siddabot:open',open);
  placement=window.SiddabotPlacement.mount({launcher:launch,host:host,edge:0,onSuspend:function(blocked){if(blocked&&sheet)close(false);}});
  if(externalTrigger){launch.style.setProperty('display','none','important');externalTrigger.addEventListener('click',open);}
  document.addEventListener('visibilitychange',function(){if(!document.hidden&&sheet&&(questionOutbox.pending().length||questionReplies.length))reconnectQuestions();if(!document.hidden)staffInbox();});
  S.set('open',0);
})();
